import {
  DataResult,
  State as DataState,
  FilterOperator,
} from '@progress/kendo-data-query';
import {
  GridCellProps,
  GridColumnProps,
  GridDataStateChangeEvent,
  GridRowProps,
} from '@progress/kendo-react-grid';
import { TFunction } from 'i18next';
import { produce } from 'immer';
import { ReactElement, cloneElement, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import {
  formatDate,
  showErrorToast,
} from '../../../../../common/global/helpers';
import useBreakpoints from '../../../../../common/hooks/useBreakpoints';
import Breadcrumbs from '../../../../../common/widgets/Breadcrumbs';
import { DataLoader } from '../../../../../common/widgets/DataLoader';
import Grid, { GridCellTd, getSkip } from '../../../../../common/widgets/Grid';
import Icons from '../../../../../common/widgets/Icons';
import Loader from '../../../../../common/widgets/Loader';
import LoadingSpinner from '../../../../../common/widgets/LoadingSpinner';
import { config } from '../../../config';
import { CustomerInfoContext } from '../../../contexts/CustomerInfoContext';
import { OrderStatuses } from '../../../enums';
import { VoucherItemModel } from '../../../models/Vouchers';
import { resources } from '../../../resources/resources';
import { voucherService } from '../../../services/vouchers';
import applicationCrumbs from '../../ApplicationCrumbs';
import { EmptyStateVouchers } from '../../Common/EmptyStates';
import PlatformInformation from '../../Common/PlatformInformation';
import MyAccountMenu from '../Common/MyAccountMenu';
import { VouchersTabNav } from './Common/VouchersTabNav';
import { DownloadVoucherDialog } from './DownloadVoucherDialog';

export enum VouchersTabs {
  Active = 'active',
  Used = 'used',
}

const initialActiveTab = VouchersTabs.Active;

interface VouchersPageState {
  activeTab: VouchersTabs;
  vouchers?: VoucherItemModel[];
  total: number;
  dataState: DataState;
  voucherDialog?: VoucherItemModel;
  isLoading: boolean;
  showErrorPanel?: boolean;
  togglingKeys: string[];
}

const formatVouchers = (data: VoucherItemModel[]): VoucherItemModel[] => {
  return data.map(e => ({
    ...e,
    date: formatDate(e.date),
  }));
};

const buildDataState = (tab: VouchersTabs): DataState => ({
  skip: 0,
  take: config.grid.defaultPageSize,
  sort: [{ field: 'date', dir: 'desc' }],
  filter: {
    logic: 'and',
    filters: [
      {
        field: 'orderStatusId',
        operator: FilterOperator.NotEqualTo,
        value: OrderStatuses.Failed,
      },
      {
        field: 'isVoucherUsed',
        operator: FilterOperator.EqualTo,
        value: tab === VouchersTabs.Used,
      },
    ],
  },
});

const getActiveTab = (queryString: string | null): VouchersTabs => {
  if (queryString === VouchersTabs.Used) return VouchersTabs.Used;
  return VouchersTabs.Active;
};

export type ToggleUsageTarget = Pick<
  VoucherItemModel,
  'orderProductId' | 'codeType' | 'codeIntId' | 'codeStringId' | 'isVoucherUsed'
>;

const getVoucherKey = (v: ToggleUsageTarget): string =>
  `${v.orderProductId}|${v.codeType}|${v.codeIntId ?? ''}|${
    v.codeStringId ?? ''
  }`;

export const VouchersPage = () => {
  const { t: translate } = useTranslation();
  const crumbs = applicationCrumbs(translate);
  const [searchParams] = useSearchParams();
  const activeTabQueryString = searchParams.get('activeTab');
  const odataString = searchParams.get('dataState');
  const customerInfo = useContext(CustomerInfoContext);
  const navigate = useNavigate();
  const htmlElement = document.getElementsByTagName('html')[0];
  const { isTablet } = useBreakpoints();

  const hasInitializedUrl = useRef(false);
  const prevValues = useRef({
    activeTab: activeTabQueryString,
    odataString,
  });

  const [state, setState] = useState<VouchersPageState>({
    activeTab: getActiveTab(activeTabQueryString),
    isLoading: true,
    total: 0,
    dataState: odataString
      ? JSON.parse(decodeURI(odataString))
      : buildDataState(getActiveTab(activeTabQueryString)),
    togglingKeys: [],
  });

  useEffect(() => {
    if (!hasInitializedUrl.current && !activeTabQueryString && !odataString) {
      hasInitializedUrl.current = true;
      navigate(
        `/my-account/vouchers?activeTab=${initialActiveTab}&dataState=${encodeURI(
          JSON.stringify(buildDataState(initialActiveTab)),
        )}`,
        { replace: true },
      );
      return;
    }
    hasInitializedUrl.current = true;
  }, [navigate, activeTabQueryString, odataString]);

  const onTabChange = (tab: VouchersTabs) => {
    if (tab === state.activeTab) {
      return;
    }

    setState(
      produce(d => {
        d.vouchers = undefined;
        d.isLoading = true;
      }),
    );
    navigate(
      `/my-account/vouchers?activeTab=${tab}&dataState=${encodeURI(
        JSON.stringify(buildDataState(tab)),
      )}`,
    );
  };

  const dataStateChange = (e: GridDataStateChangeEvent) => {
    navigate(
      `/my-account/vouchers?activeTab=${state.activeTab}&dataState=${encodeURI(
        JSON.stringify({
          ...e.dataState,
          skip: getSkip(state.dataState, e.dataState),
        }),
      )}`,
    );
  };

  const onDataReceived = (data: DataResult, showErrorPanel?: boolean) => {
    setState(
      produce(draft => {
        draft.vouchers = formatVouchers(data.data);
        draft.total = data.total;
        draft.isLoading = false;
        draft.showErrorPanel = showErrorPanel;
      }),
    );
  };

  const onToggleUsage = async (
    voucher: ToggleUsageTarget,
  ): Promise<boolean> => {
    const key = getVoucherKey(voucher);

    setState(
      produce(d => {
        d.togglingKeys.push(key);
      }),
    );

    const resp = await voucherService.markVoucherUsage(
      voucher.orderProductId,
      voucher.codeType,
      voucher.codeIntId,
      voucher.codeStringId,
      !voucher.isVoucherUsed,
    );

    if (resp.hasErrors) {
      showErrorToast(translate(resources.vouchers.cannotMarkUsage));
      setState(
        produce(d => {
          d.togglingKeys = d.togglingKeys.filter(k => k !== key);
        }),
      );
      return false;
    }

    setState(
      produce(d => {
        if (d.vouchers) {
          d.vouchers = d.vouchers.filter(
            v =>
              !(
                v.orderProductId === voucher.orderProductId &&
                v.codeType === voucher.codeType &&
                v.codeIntId === voucher.codeIntId &&
                v.codeStringId === voucher.codeStringId
              ),
          );
          d.total = Math.max(0, d.total - 1);
        }
        d.togglingKeys = d.togglingKeys.filter(k => k !== key);
      }),
    );

    return true;
  };

  const openVoucherDialog = (voucher: VoucherItemModel) => {
    setState(
      produce(d => {
        d.voucherDialog = voucher;
      }),
    );
    htmlElement.classList.add('dialog-active');
  };

  const rowRender = (
    row: ReactElement<HTMLTableRowElement>,
    rowProps: GridRowProps,
  ) => {
    if (!isTablet || customerInfo.customerInfo.isImpersonating) return row;

    return cloneElement(row as ReactElement<any>,{
      ...row.props,
      onClick: () => openVoucherDialog(rowProps.dataItem),
    });
  };

  useEffect(() => {
    const newActiveTab = getActiveTab(activeTabQueryString);
    const tabChanged = prevValues.current.activeTab !== activeTabQueryString;

    setState(
      produce(d => {
        d.activeTab = newActiveTab;
        if (tabChanged) {
          d.vouchers = undefined;
          d.isLoading = true;
        }
      }),
    );
    prevValues.current.activeTab = activeTabQueryString;
  }, [activeTabQueryString]);

  useEffect(() => {
    if (odataString) {
      const newDataState = JSON.parse(decodeURI(odataString));
      let isPaginationOnly = false;
      const prev = prevValues.current.odataString;

      if (prev) {
        try {
          const prevDataState = JSON.parse(decodeURI(prev));
          isPaginationOnly =
            JSON.stringify(prevDataState.sort) ===
              JSON.stringify(newDataState.sort) &&
            JSON.stringify(prevDataState.filter) ===
              JSON.stringify(newDataState.filter) &&
            (prevDataState.skip !== newDataState.skip ||
              prevDataState.take !== newDataState.take);
        } catch {
          isPaginationOnly = false;
        }
      }

      setState(
        produce(d => {
          d.dataState = newDataState;
          if (!isPaginationOnly) {
            d.vouchers = undefined;
            d.isLoading = true;
          }
        }),
      );
    }
    prevValues.current.odataString = odataString;
  }, [odataString]);

  if (!hasInitializedUrl.current) {
    return null;
  }

  return (
    <>
      <Breadcrumbs
        entries={[crumbs.home, crumbs.accountDashboard, crumbs.vouchers]}
      />
      <section className="section-main section-actions my-account-page">
        <div className="container">
          <div className="area-actions">
            <MyAccountMenu />
          </div>
          <div className="area-content">
            <PlatformInformation />
            <div className="panel padding-xl">
              <div className="page-actions">
                <h1>{translate(resources.vouchers.title)}</h1>
              </div>

              <div className="acc-tabs-container">
                <VouchersTabNav
                  activeTab={state.activeTab}
                  onTabChange={onTabChange}
                />
              </div>

              <DataLoader
                getData={voucherService.getVouchers}
                dataState={state.dataState}
                onDataReceived={onDataReceived}
              />

              <Loader
                loading={state.isLoading}
                loader={<LoadingSpinner />}
                showErrorPanel={state.showErrorPanel}
              >
                {state?.vouchers?.length ? (
                  <div
                    className={`my-account-grid ${
                      customerInfo.customerInfo.isImpersonating
                        ? 'acc-vouchers-grid-impersonating'
                        : 'acc-vouchers-grid'
                    }`}
                  >
                    <Grid
                      data={state.vouchers}
                      pageable={{
                        pageSizes: config.grid.pageSizes,
                        pageSizeValue: state.dataState.take,
                        info: false,
                        previousNext: true,
                      }}
                      {...state.dataState}
                      onDataStateChange={dataStateChange}
                      rowRender={rowRender}
                      total={state.total}
                      sortable={{ allowUnsort: true, mode: 'multiple' }}
                      columns={GetColumns(
                        customerInfo.customerInfo.isImpersonating,
                        state.activeTab,
                        state.togglingKeys,
                        onToggleUsage,
                        translate,
                        openVoucherDialog,
                      )}
                    />
                  </div>
                ) : (
                  <EmptyStateVouchers />
                )}
              </Loader>
            </div>
          </div>
        </div>
      </section>
      {state.voucherDialog && (
        <DownloadVoucherDialog
          productId={state.voucherDialog.productId}
          orderId={state.voucherDialog.orderId}
          codeType={state.voucherDialog.codeType}
          codeIntId={state.voucherDialog.codeIntId}
          codeStringId={state.voucherDialog.codeStringId}
          activeTab={state.activeTab}
          onToggleUsage={onToggleUsage}
          closeDialog={() => {
            setState(
              produce(d => {
                d.voucherDialog = undefined;
              }),
            );
            htmlElement.classList.remove('dialog-active');
          }}
        />
      )}
    </>
  );
};

const DateCell = ({ dataItem }: GridCellProps) => (
  <GridCellTd>{dataItem.date}</GridCellTd>
);

const ProductCell = ({ dataItem }: GridCellProps) => (
  <GridCellTd>{dataItem.product}</GridCellTd>
);

const OrderCell = ({ dataItem }: GridCellProps) => (
  <GridCellTd>#{dataItem.orderId}</GridCellTd>
);

interface DownloadCellProps extends GridCellProps {
  onClick: (dataItem: any) => void;
}

const DownloadCell = ({ dataItem, onClick }: DownloadCellProps) => {
  const { t: translate } = useTranslation();
  return (
    <GridCellTd className="grid-actions-cell">
      <span className="grid-actions">
        <button
          className="btn-link grid-action-nav"
          onClick={() => onClick(dataItem)}
        >
          <span className="btn-icon">
            <Icons.ArrowButton />
          </span>
          <span className="btn-text">
            {translate(resources.vouchers.download)}
          </span>
        </button>
      </span>
    </GridCellTd>
  );
};

interface UsageActionCellProps extends GridCellProps {
  activeTab: VouchersTabs;
  togglingKeys: string[];
  onToggleUsage: (voucher: ToggleUsageTarget) => Promise<boolean>;
}

const UsageActionCell = ({
  dataItem,
  activeTab,
  togglingKeys,
  onToggleUsage,
}: UsageActionCellProps) => {
  const { t: translate } = useTranslation();
  const voucher = dataItem as VoucherItemModel;
  const isToggling = togglingKeys.includes(getVoucherKey(voucher));

  const label =
    activeTab === VouchersTabs.Active
      ? translate(resources.vouchers.markAsUsed)
      : translate(resources.vouchers.markAsUnused);

  return (
    <GridCellTd className="grid-actions-cell">
      <span className="grid-actions">
        <button
          className="btn-link grid-action-nav"
          disabled={isToggling}
          onClick={() => onToggleUsage(voucher)}
        >
          <span className="btn-icon">
            <Icons.ArrowButton />
          </span>
          <span className="btn-text">{label}</span>
        </button>
      </span>
    </GridCellTd>
  );
};

const GetColumns = (
  isImpersonating: boolean,
  activeTab: VouchersTabs,
  togglingKeys: string[],
  onToggleUsage: (voucher: ToggleUsageTarget) => Promise<boolean>,
  translate: TFunction,
  openVoucherDialog: (voucher: VoucherItemModel) => void,
): GridColumnProps[] => {
  const columns: GridColumnProps[] = [
    {
      field: 'orderId',
      sortable: false,
      title: translate(resources.vouchers.orderId),
      cell: OrderCell,
    },
    {
      field: 'date',
      sortable: true,
      title: translate(resources.vouchers.date),
      cell: DateCell,
    },
    {
      field: 'product',
      sortable: false,
      title: translate(resources.vouchers.product),
      cell: ProductCell,
    },
  ];

  if (!isImpersonating) {
    columns.push(
      {
        headerClassName: 'grid-actions-cell',
        sortable: false,
        cell: props => (
          <UsageActionCell
            {...props}
            activeTab={activeTab}
            togglingKeys={togglingKeys}
            onToggleUsage={onToggleUsage}
          />
        ),
      },
      {
        headerClassName: 'grid-actions-cell',
        sortable: false,
        cell: props => <DownloadCell {...props} onClick={openVoucherDialog} />,
      },
    );
  }

  return columns;
};
